/**
 * Doi ma that bai cua API native thanh mot cau NOI DUOC PHAI LAM GI.
 *
 * ============================================================================
 *  VI SAO PHAN NAY DANG MOT FILE RIENG
 *
 *  Tren may vua cai xong, hai trong nam loai captcha se KHONG giai duoc — `hashgrid`
 *  thieu tu dien pHash, `rotate` co model nhung chua qua nguong phuc vu. Ca hai deu tra
 *  ve dung mot ma: `no_solver`.
 *
 *  Neu trang playground chi hien "khong giai duoc" thi nguoi dung se ket luan san pham
 *  hong. Su that thi ho dang thieu MOT BUOC CHUAN BI, va buoc do khac nhau cho tung
 *  solver. Khoang cach giua hai ket luan do la ca mot lan cai dat bi bo do.
 *
 *  Chu thich viet tieng Viet KHONG dau (quy uoc muc 17), con van ban HIEN THI ben duoi
 *  thi co dau — hai doi tuong doc khac nhau.
 * ============================================================================
 */

import type { NativeType } from './types';

export interface ReasonHelp {
  /** Cau ngan hien canh ma loi. */
  summary: string;
  /** Viec cu the can lam, theo thu tu. */
  steps: string[];
  /** true = gan nhu chac chan la thieu chuan bi, khong phai loi he thong. */
  needsSetup: boolean;
}

const SETUP_BY_TYPE: Partial<Record<NativeType, ReasonHelp>> = {
  image: {
    summary: 'Solver `ocr` cần một model ONNX đã được KÍCH HOẠT.',
    steps: [
      'Mở trang Model — kiểm tra có model nào của solver `ocr` đang bật không.',
      'Chưa có thì phải train: xem training/README.md (sinh dữ liệu tổng hợp rồi train CRNN+CTC).',
      'Có model rồi thì kiểm trang Profile: profile cho ImageToTextTask phải ở mode `live`.',
      'Model dưới ngưỡng chất lượng sẽ bị registry TỪ CHỐI kích hoạt — đó là chủ ý, không phải lỗi.',
    ],
    needsSetup: true,
  },
  grid: {
    summary: 'Solver `hashgrid` trả lời bằng cách TRA BẢNG, nên nó cần từ điển pHash trước.',
    steps: [
      'Bật profile tương ứng sang mode `shadow` để mẫu tự chảy vào hệ thống.',
      'Mở trang Captcha lạ — ảnh được gom nhóm sẵn theo pHash, gán nhãn cả nhóm một lần.',
      'Mở trang Từ điển hash, bấm dựng lại từ điển.',
      'Một nhãn chỉ vào từ điển khi có ≥ 3 lần xuất hiện nhất quán — bấm nhầm một lần không đầu độc được.',
    ],
    needsSetup: true,
  },
  rotate: {
    summary: 'Solver `rotate` cần model riêng cho KHO ẢNH của chính site đó.',
    steps: [
      'Model `rotate` học "ảnh THẲNG của kho ảnh này trông thế nào" — model train trên kho ảnh khác không dùng được.',
      'Sinh dữ liệu: python training/synth/gen_rotate.py --src <kho-ảnh-của-site> --out data/rotate',
      'Train: python training/train_rotate.py --data data/rotate --out data/models/rotate-v1',
      'Đo độc lập rồi nạp vào registry. Model dưới ngưỡng sẽ bị từ chối kích hoạt.',
    ],
    needsSetup: true,
  },
  text: {
    summary: 'Solver `text` không cần model — nhiều khả năng profile đang tắt.',
    steps: [
      'Mở trang Profile, tìm profile cho TextCaptchaTask (mặc định là `text-question`).',
      'Đổi mode sang `live`. Mọi profile được seed đều bắt đầu ở `off` — có chủ ý.',
      'Nếu profile đã live mà vẫn không giải được thì câu hỏi nằm ngoài phạm vi solver: nó làm toán (số và chữ), số La Mã, và câu hỏi về cấu trúc chuỗi.',
    ],
    needsSetup: true,
  },
  pow: {
    summary: 'Solver `pow` tự chứng minh được mình đúng — thất bại ở đây thường là do challenge.',
    steps: [
      'Kiểm tra profile cho CapforgePowTask đã ở mode `live` chưa.',
      'Challenge phải là JSON NGUYÊN VẸN lấy từ endpoint của site (với Altcha là /altcha/challenge).',
      'capforge KHÔNG tự gọi endpoint đó — làm vậy là mở một đường SSRF vào mạng nội bộ của bạn.',
      'Chỉ Altcha được hỗ trợ. Friendly Captcha và Prosopo trả null kèm log nói rõ lý do, không đoán bừa.',
    ],
    needsSetup: true,
  },
};

const GENERIC: Record<string, ReasonHelp> = {
  no_solver: {
    summary: 'Không solver nào nhận task này.',
    steps: [
      'Thường là do chưa có profile khớp, hoặc profile khớp đang ở mode `off`.',
      'Mở trang Profile để kiểm tra.',
    ],
    needsSetup: true,
  },
  low_confidence: {
    summary: 'Solver CÓ đáp án nhưng không đủ tự tin, nên bỏ qua thay vì trả bừa.',
    steps: [
      'Đây là thiết kế, không phải lỗi: trả một đáp án sai làm hỏng lần submit trên site đích.',
      'Muốn nhận thêm thì hạ `minConfidence` của profile — nhưng hãy đo tỉ lệ đúng trước khi hạ.',
      'Với model generic (không phải model riêng của profile) thì ngưỡng còn cao hơn hẳn.',
    ],
    needsSetup: false,
  },
  timeout: {
    summary: 'Hết thời gian trước khi có đáp án.',
    steps: [
      'Tăng `timeoutMs` trong ô Tuỳ chọn bên dưới.',
      'Với `pow`, thời gian phụ thuộc `maxnumber` của challenge — con số lớn thì cần nhiều giây.',
    ],
    needsSetup: false,
  },
  upstream_disabled: {
    summary: 'Không solver nội bộ nào giải được, và đường lui trả phí đang TẮT.',
    steps: [
      'Mặc định tắt là có chủ ý: capforge phải có giá trị ngay cả khi không cấu hình dịch vụ trả phí nào.',
      'Muốn bật thì vào trang Upstream.',
    ],
    needsSetup: true,
  },
  invalid_request: {
    summary: 'Request không hợp lệ — xem thông điệp cụ thể bên dưới.',
    steps: ['Đọc `message` do server trả về, nó nói rõ thiếu trường nào.'],
    needsSetup: false,
  },
  unsupported_type: {
    summary: 'Loại captcha này chưa được hỗ trợ.',
    steps: ['Xem docs/api.md để biết các loại đang có.'],
    needsSetup: false,
  },
  unauthorized: {
    summary: 'Key không hợp lệ hoặc thiếu scope `solve`.',
    steps: [
      'API native đòi một key có scope `solve`.',
      'Dùng nút "Tạo key thử nghiệm" ở đầu trang để tạo một key đúng chuẩn.',
    ],
    needsSetup: false,
  },
  rate_limited: {
    summary: 'Vượt giới hạn request mỗi phút của key này.',
    steps: ['Đợi một chút, hoặc tăng `rateLimitPerMin` của key ở trang API key.'],
    needsSetup: false,
  },
  quota_exceeded: {
    summary: 'Hết quota ngày của key này.',
    steps: ['Tăng `dailyQuota` ở trang API key, hoặc đợi sang ngày hôm sau.'],
    needsSetup: false,
  },
  internal_error: {
    summary: 'Lỗi nội bộ của server.',
    steps: ['Mở trang Log trực tiếp và bật công tắc lên, rồi thử lại — dòng log sẽ nói rõ chuyện gì.'],
    needsSetup: false,
  },
};

/**
 * Chon loi giai thich cu the nhat co the.
 *
 * `no_solver` la ma dung chung cho ca "chua co model", "chua co tu dien" va "profile
 * dang tat" — ba tinh huong doi hoi ba viec khac han. Vi vay khi gap ma do, ta uu tien
 * loi giai thich theo LOAI CAPTCHA thay vi cau chung chung.
 */
export function explainReason(reason: string, type: NativeType): ReasonHelp {
  if (reason === 'no_solver') {
    const specific = SETUP_BY_TYPE[type];
    if (specific !== undefined) return specific;
  }
  return (
    GENERIC[reason] ?? {
      summary: `Mã lỗi không rõ: ${reason}`,
      steps: ['Mở trang Log trực tiếp để xem chuyện gì đã xảy ra.'],
      needsSetup: false,
    }
  );
}
